import { useCallback, useEffect, useRef, useState } from 'react';

import { useUnit } from 'effector-react';
import { useLocation } from 'react-router-dom';

import { fetchNearbyCiaLanes } from '@m/hooks/data/customer/gateAccess';
import { useCustomerSubscription } from '@m/hooks/data/customer/subscriptions';
import { useGeolocation } from '@m/hooks/useGeolocation';
import { type CiaLaneView } from '@m/types/api';
import { userStore } from 'apps/customer/state/user';
import {
  getCiaNearbyPromptVariant,
  groupCiaLanesBySite,
  isCiaNearbyCooldownActive,
  stampCiaNearbyLastCheckedAt,
  type CiaNearbyPromptVariant,
  type CiaSiteLaneGroup,
} from 'apps/customer/utils/ciaNearbyGatePrompt';
import { matchGateAccessPath } from 'apps/customer/utils/gateAccessPath';

export type UseCiaNearbyGatePromptResult = {
  dismiss: () => void;
  groups: CiaSiteLaneGroup[];
  isOpen: boolean;
  lanes: CiaLaneView[];
  variant: CiaNearbyPromptVariant | null;
};

/**
 * Nearby CIA gate discovery for enterprise subscribers, triggered on tab visibility
 * and on route changes so it can surface anywhere in the app.
 * One-shot GPS + lanes fetch per attempt; 3-minute cooldown between attempts.
 */
export function useCiaNearbyGatePrompt(): UseCiaNearbyGatePromptResult {
  const { authenticated } = useUnit(userStore);
  const { pathname } = useLocation();
  const { requestLocation } = useGeolocation();
  const { data: subscriptions, isSuccess: subscriptionsLoaded } = useCustomerSubscription({
    enabled: !!authenticated,
  });

  const isEnterpriseSubscriber =
    subscriptions?.some((subscription) => !!subscription.enterpriseDetails) ?? false;

  const [lanes, setLanes] = useState<CiaLaneView[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const inFlightRef = useRef(false);
  const isOpenRef = useRef(false);
  const pathnameRef = useRef(pathname);
  const authenticatedRef = useRef(authenticated);
  const subscriptionsLoadedRef = useRef(subscriptionsLoaded);
  const isEnterpriseSubscriberRef = useRef(isEnterpriseSubscriber);

  useEffect(() => {
    isOpenRef.current = isOpen;
    pathnameRef.current = pathname;
    authenticatedRef.current = authenticated;
    subscriptionsLoadedRef.current = subscriptionsLoaded;
    isEnterpriseSubscriberRef.current = isEnterpriseSubscriber;
  }, [authenticated, isEnterpriseSubscriber, isOpen, pathname, subscriptionsLoaded]);

  const dismiss = useCallback(() => {
    setIsOpen(false);
  }, []);

  const isOnGateAccessPath = matchGateAccessPath(pathname) != null;

  const isAttemptStale = useCallback(() => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return true;
    if (matchGateAccessPath(pathnameRef.current)) return true;
    if (
      !authenticatedRef.current ||
      !subscriptionsLoadedRef.current ||
      !isEnterpriseSubscriberRef.current
    ) {
      return true;
    }
    if (isOpenRef.current) return true;
    return false;
  }, []);

  const runCheck = useCallback(async () => {
    if (inFlightRef.current) return;
    if (isAttemptStale()) return;
    if (isCiaNearbyCooldownActive()) return;

    inFlightRef.current = true;
    try {
      const coords = await requestLocation();
      const response = await fetchNearbyCiaLanes({
        latitude: coords.latitude,
        longitude: coords.longitude,
      });

      // GPS/lanes can take seconds; skip cooldown and the sheet if the user
      // left the tab, entered gate-access, or is no longer eligible.
      if (isAttemptStale()) return;

      stampCiaNearbyLastCheckedAt();

      if (response.lanes.length === 0) {
        setLanes([]);
        setIsOpen(false);
        return;
      }

      setLanes(response.lanes);
      setIsOpen(true);
    } catch {
      // Denied GPS / timeouts / lanes errors still count as an attempt so
      // route changes do not retry location every navigation.
      stampCiaNearbyLastCheckedAt();
      setLanes([]);
      setIsOpen(false);
    } finally {
      inFlightRef.current = false;
    }
  }, [isAttemptStale, requestLocation]);

  useEffect(() => {
    if (!isOnGateAccessPath) return;
    setIsOpen(false);
  }, [isOnGateAccessPath]);

  useEffect(() => {
    if (!authenticated || !subscriptionsLoaded || !isEnterpriseSubscriber) {
      return undefined;
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void runCheck();
      }
    };

    if (document.visibilityState === 'visible') {
      void runCheck();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [authenticated, isEnterpriseSubscriber, pathname, runCheck, subscriptionsLoaded]);

  const groups = groupCiaLanesBySite(lanes);
  const variant = getCiaNearbyPromptVariant(groups);

  return {
    dismiss,
    groups,
    isOpen: isOpen && variant != null,
    lanes,
    variant,
  };
}
